import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatINR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ClipboardList } from "lucide-react";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { StatusPill } from "@/components/StatusPill";
import { usePhrase } from "@/contexts/LanguageContext";

export default function ApplicationDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [responseText, setResponseText] = useState("");
  const pt = usePhrase();
  const load = async () => {
    const { data } = await api.get(`/applications/${id}`); setData(data);
    try {
      const p = await api.get(`/applications/${id}/improvement-plan`); setPlan(p.data);
    } catch (e) {
      // Improvement plan is only present for rejected applications; 404 is expected otherwise.
      if (e?.response?.status !== 404 && process.env.NODE_ENV !== "production") {
        console.warn("improvement-plan fetch failed:", e?.message);
      }
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const respond = async (qid) => {
    try { await api.post(`/queries/${qid}/respond`, { response_text: responseText }); toast.success(pt("Response sent")); setResponseText(""); await load(); } catch (e) { toast.error(pt("Failed")); }
  };

  if (!data) return <div className="text-slate-500">{pt("Loading…")}</div>;
  const { application, history, queries, sanction, rejection, disbursements, lender, business } = data;

  return (
    <div className="space-y-6" data-testid={`app-detail-${id}`}>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{pt("Application")}{application.lender_ref_no ? ` · ${application.lender_ref_no}` : ""}</div>
        <h1 className="text-2xl font-bold text-slate-900 brand-heading flex flex-wrap items-center gap-3">{lender?.name || "—"} <StatusPill status={application.status} /></h1>
        <p className="text-slate-600 mt-1 text-sm">{pt("Business")}: {business?.business_identity?.legal_business_name || "—"}</p>
      </div>

      {/* Timeline */}
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading mb-4">{pt("Timeline")}</h2>
        <ol className="space-y-3">
          {history.map((h, idx) => (
            <li key={h.id} className="flex items-start gap-3 text-sm">
              <div className="mt-0.5">
                {idx === history.length - 1 ? <Circle className="w-4 h-4 text-orange-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
              </div>
              <div className="flex-1">
                <div className="font-medium text-slate-900">{pt(h.status)}</div>
                <div className="text-xs text-slate-500">{formatDate(h.changed_at)}{h.note ? ` · ${pt(h.note)}` : ""}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Sanction */}
      {sanction && (
        <div className="p-6 rounded-lg border border-emerald-200 bg-emerald-50" data-testid="sanction-block">
          <div className="text-xs uppercase tracking-widest text-emerald-700">{pt("Sanction")}</div>
          <div className="text-xl font-bold text-slate-900 brand-heading">{sanction.sanction_number} · {formatINR(sanction.sanctioned_amount)}</div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><div className="text-slate-500">{pt("Interest")}</div><div className="font-semibold">{sanction.interest_rate}% {pt(sanction.rate_type)}</div></div>
            <div><div className="text-slate-500">{pt("Tenure")}</div><div className="font-semibold">{sanction.tenure_months} {pt("mo")}</div></div>
            <div><div className="text-slate-500">EMI</div><div className="font-semibold">{formatINR(sanction.emi)}</div></div>
            <div><div className="text-slate-500">CGTMSE</div><div className="font-semibold">{sanction.cgtmse_status ? pt(sanction.cgtmse_status) : "—"}</div></div>
          </div>
        </div>
      )}

      {/* Disbursements */}
      {disbursements?.length > 0 && (
        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <h2 className="font-semibold text-slate-900 brand-heading mb-2">{pt("Disbursements")}</h2>
          <ul className="text-sm space-y-1">
            {disbursements.map((d) => (
              <li key={d.id} className="flex justify-between border-b border-slate-100 pb-1">
                <div>{formatDate(d.disbursement_date)} · {pt("Tranche")} {d.tranche}</div>
                <div className="font-semibold">{formatINR(d.amount)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Rejection + improvement plan */}
      {rejection && (
        <div className="p-6 rounded-lg border border-red-200 bg-red-50" data-testid="rejection-block">
          <div className="text-xs uppercase tracking-widest text-red-700">{pt("Rejection")}</div>
          <div className="text-slate-900 font-semibold">{pt(rejection.reason_category)}</div>
          <div className="text-sm text-slate-700 mt-1">{pt(rejection.borrower_visible_reason)}</div>
          {plan && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-red-700">{pt("Improvement Plan")}</div>
              <ul className="mt-1 list-disc pl-5 text-sm text-slate-700 space-y-1">
                {(plan.recommendations || []).map((r) => <li key={r}>{pt(r)}</li>)}
              </ul>
              {(plan.alternative_products || []).length > 0 && (
                <div className="mt-2 text-sm text-slate-700"><strong>{pt("Alternative products:")}</strong> {plan.alternative_products.map(pt).join(", ")}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Queries */}
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-4 h-4 text-slate-700" />
          <h2 className="font-semibold text-slate-900 brand-heading">{pt("Bank queries")}</h2>
        </div>
        <ul className="space-y-3">
          {(queries || []).filter((q) => q.visible_to_borrower !== false).map((q) => (
            <li key={q.id} className="p-4 rounded-md border border-slate-200 bg-slate-50" data-testid={`query-${q.id}`}>
              <div className="text-xs uppercase tracking-widest text-slate-500">{pt(q.category)} · {pt(q.status)}</div>
              <div className="text-sm text-slate-900 mt-1">{pt(q.description)}</div>
              {(q.responses || []).length > 0 && (
                <ul className="mt-2 text-xs text-slate-600 space-y-1">
                  {q.responses.map((r, ri) => <li key={r.id || ri}>↳ {pt(r.response_text)}</li>)}
                </ul>
              )}
              {q.status === "Open" && (
                <div className="mt-3 flex gap-2">
                  <input value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder={pt("Your response")} data-testid={`query-response-${q.id}`} className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm" />
                  <button onClick={() => respond(q.id)} data-testid={`query-send-${q.id}`} className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-1.5 text-xs font-semibold">{pt("Respond")}</button>
                </div>
              )}
            </li>
          ))}
          {queries?.length === 0 && <li className="text-sm text-slate-500">{pt("No queries.")}</li>}
        </ul>
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
